import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AuthLayout, SubmitButton } from "@/components/auth/AuthLayout";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const CODE_KEY = "gamls_verify_code";

export default function VerifyEmail() {
  const { auth, signIn, logout } = useApp();
  const [params] = useSearchParams();
  const next = params.get("next") || "/my";
  const linkCode = (params.get("code") || "").replace(/\D/g, "").slice(0, 6);
  if (linkCode) sessionStorage.setItem(CODE_KEY, linkCode);
  const [code, setCode] = useState(() => linkCode || sessionStorage.getItem(CODE_KEY) || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const nav = useNavigate();
  if (auth === null) return <div className="min-h-screen grid place-items-center text-gray-500">Loading…</div>;
  if (auth?.kind !== "member") return <Navigate to={`/login?next=${encodeURIComponent("/my")}`} replace />;
  if (auth.email_verified) { sessionStorage.removeItem(CODE_KEY); return <Navigate to={next} replace />; }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/members/verify", { code });
      signIn(r.data);
      sessionStorage.removeItem(CODE_KEY);
      toast.success("Account confirmed");
      nav(next);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  const info = { channel: auth.verify_channel || "email", sms_codes_left: auth.sms_codes_left ?? 0, email: auth.email, phone_hint: auth.phone_hint, ...sent };
  const bySms = info.channel === "sms";
  const resend = async (channel) => {
    try {
      const r = await api.post("/members/resend-code", null, { params: channel ? { channel } : {} });
      setSent(r.data);
      toast.success(r.data.channel === "sms" ? `A new code has been sent by SMS to ${r.data.phone_hint}` : `A new code has been emailed to ${r.data.email}`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthLayout blurb="One quick step: confirm your account so we can send your receipts and updates.">
      <form onSubmit={submit} data-testid="verify-email-form">
        <div className="w-11 h-11 rounded-md bg-[#FFF0F0] text-[#E60000] grid place-items-center mb-4"><MailCheck className="w-6 h-6" /></div>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight">Confirm your account</h2>
        <p className="text-sm text-gray-500 mt-1" data-testid="verify-sent-to">We sent a 6-digit code {bySms ? <>by SMS to <strong className="text-[#111]">{info.phone_hint}</strong></> : <>to <strong className="text-[#111]">{info.email}</strong></>}. It expires in 30 minutes.</p>
        {linkCode && <p className="text-sm text-emerald-700 mt-3" data-testid="verify-code-prefilled">Your code has been filled in. Tap Confirm.</p>}
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="000000"
          className="h-14 mt-8 text-center font-mono text-2xl tracking-[0.5em]" data-testid="verify-code-input" />
        <div className="mt-5"><SubmitButton busy={busy || code.length !== 6} testId="verify-submit">{busy ? "Checking…" : "Confirm"}</SubmitButton></div>
        <div className="flex flex-wrap justify-between gap-2 mt-4 text-sm">
          <div className="flex flex-col gap-1">
            <button type="button" onClick={() => resend("email")} className="font-semibold text-[#E60000] hover:underline text-left" data-testid="verify-resend">Send a new code by email</button>
            {info.sms_codes_left > 0 ? <button type="button" onClick={() => resend("sms")} className="font-semibold text-[#E60000] hover:underline text-left" data-testid="verify-resend-sms">Send by SMS instead ({info.sms_codes_left} left)</button>
              : <span className="text-xs text-gray-500" data-testid="verify-sms-used">SMS codes used up. Use email.</span>}
          </div>
          <button type="button" onClick={async () => { await logout(); nav("/"); }} className="text-gray-500 hover:text-[#E60000]" data-testid="verify-signout">Sign out</button>
        </div>
      </form>
    </AuthLayout>
  );
}
